// @ts-nocheck
import { Asset, FirmAssetBalanceStatistics } from '@tassat/fred-definitions';
import { AssetWithPrice } from '@tassat/fred-extended-definitions';
import { formatAsAsset, formatAsAssetNoSign } from '@tassat/utility';

import { getAccountPositionsStatsByAccountId } from 'Selectors/accountPositions';

export interface AssetBalanceColumnDef {
    header: string;
    assetId: string;
    valuePath: keyof Pick<FirmAssetBalanceStatistics, 'AvailablePosition' | 'CurrentPosition'>;
    price: string | undefined;
    formatAsAssetNoSign: boolean;
    ccy?: Asset;
    security?: Asset;
}

export const ASSET_BALANCE_FIELDS: { valuePath: AssetBalanceColumnDef['valuePath']; label: string }[] = [
    { valuePath: 'AvailablePosition', label: 'Available Balance' },
    { valuePath: 'CurrentPosition', label: 'Current Balance' }
];

export const buildCcyColumnDefs = (asset: AssetWithPrice): AssetBalanceColumnDef[] => ASSET_BALANCE_FIELDS.map(({ valuePath, label }) => ({
    header: `${asset.Ticker} ${label}`,
    assetId: asset.AssetId,
    valuePath,
    price: undefined,
    formatAsAssetNoSign: false,
    ccy: asset
}));

export const buildSecurityColumnDefs = (asset: AssetWithPrice, currencyAsset: AssetWithPrice | undefined): AssetBalanceColumnDef[] => (
    ASSET_BALANCE_FIELDS.flatMap(({ valuePath, label }) => [
        {
            header: `${asset.Ticker} ${label}`,
            assetId: asset.AssetId,
            valuePath,
            price: undefined,
            formatAsAssetNoSign: true,
            ccy: currencyAsset,
            security: asset
        },
        {
            header: `${asset.Ticker} ${label} Equivalent Value`,
            assetId: asset.AssetId,
            valuePath,
            price: asset.Price,
            formatAsAssetNoSign: false,
            ccy: currencyAsset,
            security: asset
        }
    ])
);

// New assets get a fixed column position by adding their ticker here: USD leads,
// then TFND, then FTIXX. Anything not listed (another USD security, or another
// currency entirely) keeps its natural order, grouped with its own currency's assets -
// each security's equivalent-value column is paired with the Ccy asset in its own
// group, not hardcoded to USD, so a second (or third...) currency works too.
export const buildAssetBalanceColumnDefs = (currencyGroups): AssetBalanceColumnDef[] => {
    const COLUMN_ORDER = [ 'USD', 'TFND', 'FTIXX' ];
    const rank = (ticker) => {
        const index = COLUMN_ORDER.indexOf(ticker);

        return index === -1 ? COLUMN_ORDER.length : index;
    };

    const assetPairs = currencyGroups.flatMap(({ ccyAssets, securityAssets }) => {
        const currencyAsset = Object.values(ccyAssets)[0];

        return [
            ...Object.values(ccyAssets)
                .map(asset => ({ asset, currencyAsset: undefined })),
            ...Object.values(securityAssets)
                .map(asset => ({ asset, currencyAsset }))
        ];
    });

    return assetPairs
        .sort((a, b) => rank(a.asset.Ticker) - rank(b.asset.Ticker))
        .flatMap(({ asset, currencyAsset }) => (asset.Type === 'Ccy' ? buildCcyColumnDefs(asset) : buildSecurityColumnDefs(asset, currencyAsset)));
};

// getAccountPositionsStatsByAccountId returns one entry per currency group (one per distinct
// Curcode); merging them here handles an account holding more than one currency - AssetIds
// are unique globally, so merging by key is safe.
const getMergedAccountPositionBalance = (state, accountId: string) => getAccountPositionsStatsByAccountId(state, accountId)
    .reduce((acc, group) => ({
        ccy: { ...acc.ccy, ...group.ccy },
        security: { ...acc.security, ...group.security }
    }), {
        ccy: {},
        security: {}
    });

export const getAssetBalanceColumnValues = (state, accountId: string, columnDefs: AssetBalanceColumnDef[]): string[] => {
    const accountPositionBalance = getMergedAccountPositionBalance(state, accountId);

    return columnDefs.map((col) => {
        const balanceMap = col.security ? accountPositionBalance.security : accountPositionBalance.ccy;
        const entry = balanceMap[col.assetId];
        const isEquivalentValue = col.price !== undefined;
        const isAvailable = col.valuePath === 'AvailablePosition';
        const amount = entry ?
            (isEquivalentValue ?
                (isAvailable ? entry.equivalentAvailableBalance : entry.equivalentCurrentBalance)
                : (isAvailable ? entry.availableBalance : entry.currentBalance))
            : 0;

        return col.formatAsAssetNoSign ?
            `"${formatAsAssetNoSign(amount, col.security)}"`
            : `"${formatAsAsset(amount, col.ccy, 'symbol')}"`;
    });
};